<template>
  <slot></slot>
</template>

<script setup lang="ts">
/* eslint-disable max-len */
/* eslint-disable no-mixed-operators */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { gsap } from 'gsap'
import { InertiaPlugin } from 'gsap/InertiaPlugin'

import { ResponsiveLabel, useResize, useResponsive } from '@/utils/aware'

gsap.registerPlugin(InertiaPlugin)

const props = defineProps({
  selector: {
    type: String,
    default: '[data-draggable]',
  },
  activeAfter: {
    type: String,
    default: 's',
  },
})

const isInitialized = ref(false)
const isActive = ref(false)

useResponsive('greater', props.activeAfter as ResponsiveLabel, value => {
  isActive.value = value
})

let clickCounter = 0
let shouldAnimate = true
let draggableInstances: any[] = []
let currentlyDragged: any
let currentRotation: string

const getOuterPicture = (el: HTMLElement) => {
  let parentOuter: HTMLElement | null = null
  const parent = el.parentNode as HTMLElement
  const child = el

  if (parent.classList.contains('picture-outer')) {
    parentOuter = parent
  }

  if (child.classList.contains('picture-outer')) {
    parentOuter = child
  }

  return parentOuter
}

const onPress = (self: any) => {
  clickCounter += 1
  currentlyDragged = self
  currentlyDragged.target.classList.add('is-dragging')

  const parentOuter = getOuterPicture(currentlyDragged.target)

  if (parentOuter) {
    parentOuter.style.zIndex = `${10 + clickCounter}`
  }

  shouldAnimate = true
  currentRotation = self.target._gsap?.rotation.replace('deg', '')
  currentlyDragged.target.status = 'dragging'
  currentlyDragged.target.newX =
    currentlyDragged.pointerX - currentlyDragged.target.offsetWidth / 2
  currentlyDragged.target.newY =
    currentlyDragged.pointerY - currentlyDragged.target.offsetHeight / 2
  currentlyDragged.target.newScale = 1
  currentlyDragged.target.newElevation = 30
  currentlyDragged.target.currentRotation = Number(currentRotation)
  currentlyDragged.target.newRotation = 1
  currentlyDragged.slide.newScale = 1.3
}

const onRelease = (self: any) => {
  self.target.classList.remove('is-dragging')

  const parentOuter = getOuterPicture(currentlyDragged.target)

  if (parentOuter) {
    parentOuter.style.zIndex = `${10 + clickCounter}`
  }

  currentlyDragged.target.status = 'default'
  currentlyDragged.target.newElevation = 10
  currentlyDragged.slide.newScale = 1
  currentRotation &&
    (currentlyDragged.target.newRotation = Number(currentRotation))
}

const onMove = () => {
  if (currentlyDragged.target.status === 'dragging') {
    currentlyDragged.target.newX =
      currentlyDragged.pointerX - currentlyDragged.target.offsetWidth / 2
    currentlyDragged.target.newY =
      currentlyDragged.pointerY - currentlyDragged.target.offsetHeight / 2
  }
}

function getTranslateXY(element: HTMLElement) {
  const style = window.getComputedStyle(element)
  const matrix = new DOMMatrixReadOnly(style.transform)

  return {
    translateX: matrix.m41,
    translateY: matrix.m42,
  }
}

const animate = () => {
  if (!currentlyDragged || !shouldAnimate) {
    window.requestAnimationFrame(animate)

    return
  }

  currentlyDragged.target.currentX = getTranslateXY(
    currentlyDragged.target
  ).translateX
  currentlyDragged.target.currentY = getTranslateXY(
    currentlyDragged.target
  ).translateY
  if (currentlyDragged.target.status === 'dragging') {
    currentlyDragged.target.speedX = currentlyDragged.deltaX * 2
    currentlyDragged.target.speedY = currentlyDragged.deltaY * 2
    // currentlyDragged.target.speedX = currentlyDragged.target.newX - currentlyDragged.target.currentX
    // currentlyDragged.target.speedY = currentlyDragged.target.newY - currentlyDragged.target.currentY
  }
  currentlyDragged.target.speedX *= 0.9
  currentlyDragged.target.speedY *= 0.9

  currentlyDragged.target.currentX += currentlyDragged.target.speedX * 0.2
  currentlyDragged.target.currentY += currentlyDragged.target.speedY * 0.2

  currentlyDragged.target.currentScale +=
    (currentlyDragged.target.newScale - currentlyDragged.target.currentScale) *
    0.2
  currentlyDragged.target.currentRotation +=
    (currentlyDragged.target.newRotation -
      currentlyDragged.target.currentRotation) *
    0.13
  currentlyDragged.target.currentElevation +=
    (currentlyDragged.target.newElevation -
      currentlyDragged.target.currentElevation) *
    0.2

  const shadowDist = currentlyDragged.target.currentElevation
  const shadowBlur = (currentlyDragged.target.currentElevation - 10) * 1.5
  const shadowOpacity = 0.2 - currentlyDragged.target.currentElevation / 500

  currentlyDragged.slide.currentScale +=
    (currentlyDragged.slide.newScale - currentlyDragged.slide.currentScale) *
    0.2
  currentlyDragged.target.speedX &&
    (currentlyDragged.target.angleY += currentlyDragged.target.speedX * 0.02)
  currentlyDragged.target.speedY &&
    (currentlyDragged.target.angleX -= currentlyDragged.target.speedY * 0.02)

  currentlyDragged.target.angleY *= 0.96
  currentlyDragged.target.angleX *= 0.96

  const rotationZ =
    currentlyDragged.target.angleY + currentlyDragged.target.currentRotation

  currentlyDragged.target.style.transform = `
        translate(${currentlyDragged.x}px, ${currentlyDragged.y}px)
        rotateX(${currentlyDragged.target.angleX}deg)
        rotateY(${currentlyDragged.target.angleY}deg)
        rotateZ(${rotationZ}deg)
        scale(${currentlyDragged.target.currentScale})
      `

  currentlyDragged.target.style.boxShadow = `
      ${shadowDist}px
      ${shadowDist}px
      ${shadowBlur}px
      rgba(0, 0, 0, ${shadowOpacity})
    `

  if (Math.abs(rotationZ) < 0.001) {
    shouldAnimate = false
  }

  currentlyDragged.slide.style.transform = `translate(${
    -currentlyDragged.target.speedX * 0.3
  }px, ${-currentlyDragged.target.speedY * 0.3}px ) scale(${
    currentlyDragged.slide.currentScale
  })`
  window.requestAnimationFrame(animate)
}

const init = async () => {
  isInitialized.value = true
  const { Draggable } = await import('gsap/Draggable')

  gsap.registerPlugin(Draggable)

  draggableInstances = Draggable.create(props.selector, {
    type: 'x,y',
    inertia: true,
    zIndexBoost: true,
    throwResistance: 20000,
    onPress() {
      onPress(this)
    },
    onRelease() {
      onRelease(this)
    },
    onMove() {
      onMove()
    },
  })

  animate()

  draggableInstances.forEach((instance: any) => {
    instance.target.classList.add('is-draggable')
    instance.target.status = 'default'
    instance.target.currentX = getTranslateXY(instance.target).translateX
    instance.target.currentY = getTranslateXY(instance.target).translateY
    instance.target.newX = 0
    instance.target.newY = 0
    instance.target.speedX = 0
    instance.target.speedY = 0
    instance.target.angleX = 0
    instance.target.angleY = 0
    instance.target.currentScale = 1
    instance.target.currentRotation = 1
    instance.target.newScale = 1
    instance.target.currentElevation = 10
    instance.target.newElevation = 10
    instance.slide = instance.target.querySelector('img')
    instance.slide.currentScale = 1
    instance.slide.newScale = 1
    instance.slide.style.transform = `scale(${1})`
  })
}

const kill = () => {
  draggableInstances.forEach(instance => {
    instance.kill()
  })

  draggableInstances = []
  isInitialized.value = false
}

const onResize = () => {
  isActive.value && init()
}

useResize(() => {
  // if (isInitialized.value && !isActive.value) {
  //   kill()
  // }

  // if (!isInitialized.value && isActive.value) {
  //   init()
  // }
  onResize()
  // init()
})

onMounted(() => {
  isActive.value && init()
})

onBeforeUnmount(() => {
  kill()
})
</script>
